import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, Car, ClipboardCheck, Loader2, Megaphone } from "lucide-react";

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

export function MyFieldSafetyView() {
  const [data, setData] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ date_of_incident: new Date().toISOString().slice(0, 10), incident_location: "", description: "", incident_category: "", was_injured: false });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/my/safety").then((r) => setData(r.data)).catch(() => toast.error("Failed to load your safety data"));
    api.get("/hseq/reference").then((r) => setCats(r.data.incident_categories || r.data.categories || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.incident_location || !form.description || !form.incident_category) { toast.error("Location, category and description are required"); return; }
    setSending(true);
    try {
      await api.post("/hseq/incidents", form);
      toast.success("Incident reported — thank you");
      setReportOpen(false);
      setForm({ ...form, incident_location: "", description: "", incident_category: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSending(false); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading your safety data…</div>;

  const jsa = data.jsa || {};
  return (
    <div className="space-y-4 max-w-3xl" data-testid="my-safety-view">
      {!data.matched && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="my-safety-unlinked-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {data.warning}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> My Safety{data.employee_name ? ` — ${data.employee_name}` : ""}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Your JSA audit performance, CCEW checks and assigned vehicle</div>
        </div>
        <Button size="sm" className="bg-[#003a5c] hover:bg-[#00294a] text-white" onClick={() => setReportOpen(true)} data-testid="my-safety-report-btn">
          <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Report incident / hazard
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="JSA audits" value={jsa.total ?? 0} testid="my-jsa-total" />
        <Stat label="Checks pass rate" value={jsa.pass_rate != null ? `${jsa.pass_rate}%` : "—"}
              tone={jsa.pass_rate >= 95 ? "text-emerald-600" : jsa.pass_rate != null ? "text-amber-600" : "text-slate-400"} testid="my-jsa-passrate" />
        <Stat label="Checks failed" value={jsa.checks_failed ?? 0} tone={(jsa.checks_failed || 0) > 0 ? "text-rose-600" : "text-emerald-600"} testid="my-jsa-failed" />
      </div>

      {(data.vehicles || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="my-vehicle-card">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2"><Car className="w-4 h-4 text-slate-500" /> My vehicle{data.vehicles.length > 1 ? "s" : ""}</div>
          {data.vehicles.map((v) => (
            <div key={v.rego} className="flex items-center justify-between text-sm py-1.5 border-t border-slate-100 first:border-0">
              <span className="font-medium">{v.asset_name || v.rego}</span>
              <span className="text-xs text-slate-500">{v.last_service_date ? `serviced ${v.last_service_date}` : ""}</span>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${v.status === "ON_ROAD" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{v.status === "ON_ROAD" ? "On road" : "Off road"}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="my-jsa-recent">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2"><ClipboardCheck className="w-4 h-4 text-slate-500" /> Recent JSA audits</div>
        {(jsa.recent || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No audits recorded against your name yet.</div> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-400 uppercase tracking-wide text-[10px]"><th className="py-1">Date</th><th>Client</th><th>Ref</th><th className="text-right">Pass / Fail</th></tr></thead>
            <tbody>
              {jsa.recent.map((a, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5">{(a.completed_date || "").slice(0, 10)}</td>
                  <td className="text-slate-500">{a.client_name || "—"}</td>
                  <td className="text-slate-500">{a.form_ref_code || "—"}</td>
                  <td className={`text-right font-medium ${a.checklist_fail > 0 ? "text-rose-600" : "text-emerald-600"}`}>{a.checklist_pass} / {a.checklist_fail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.ccew?.recent || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="my-ccew-recent">
          <div className="text-sm font-semibold text-slate-800 mb-2">CCEW audits</div>
          {data.ccew.recent.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs py-1 border-t border-slate-100 first:border-0">
              <span>{c.period_month}</span><span className="text-slate-500">{c.auditor || ""}</span>
              <span className={`font-medium ${(c.result || "").toLowerCase() === "pass" ? "text-emerald-600" : "text-slate-600"}`}>{c.result || "—"}</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="my-safety-report-dialog">
          <DialogHeader><DialogTitle>Report incident / hazard</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><Input type="date" value={form.date_of_incident} onChange={(e) => setForm({ ...form, date_of_incident: e.target.value })} data-testid="report-date" /></div>
            <div><Label>Location</Label><Input value={form.incident_location} onChange={(e) => setForm({ ...form, incident_location: e.target.value })} placeholder="Site / area" data-testid="report-location" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.incident_category} onValueChange={(v) => setForm({ ...form, incident_category: v })}>
                <SelectTrigger data-testid="report-category"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {(cats.length ? cats : ["Hazard", "Near Miss", "Injury", "Property Damage", "Environmental"]).map((c) => (
                    <SelectItem key={typeof c === "string" ? c : c.value} value={typeof c === "string" ? c : c.value}>{typeof c === "string" ? c : c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>What happened?</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="report-description" /></div>
            <Button onClick={submit} disabled={sending} className="w-full bg-[#003a5c] hover:bg-[#00294a] text-white" data-testid="report-submit">
              {sending ? "Submitting…" : "Submit report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
